import { getCookie } from 'helpers/index';
import { ahuBoardApi } from './ahuBoardApi';

/**
 * Phase U — the `/user_settings/` data layer.
 *
 * Injected into `ahuBoardApi` (one reducerPath, no `store.ts` wiring) like the
 * 17 other `injectEndpoints` call sites; endpoint names carry a `UserSettings`
 * prefix so they cannot collide with anything already living in that instance.
 *
 * ★ WAVE U-a SCOPE — ONE endpoint. ★ WAVES U-b + U-c — SEVEN MORE (below).
 *
 * The page fires 15 requests on boot (discovery §7.1). Eleven of them belong to
 * the Submittal / Proposal tabs and land with those waves; three are the shared
 * header's (`release-note/`, `release-note-all/`, the mfg logo) and already
 * exist in `headerApi.ts`; `users/<id>` and `mfg/<id>` already exist in
 * `dashboardApi` / `authApi`. That leaves exactly one new call for U-a:
 * `report/colors/`, fired by the colour view (`color/view/color.js:32-36` via
 * `color/collection/colors.js`) — which is constructed on EVERY boot, not just
 * when `#color` is the active tab (discovery §3 "all three views are ALWAYS
 * constructed on load").
 *
 * ★ THE COLOUR WRITE IS NOT HERE — deliberately.
 *
 * The tab's only write is `PATCH /api/v1.0/users/<id> {"report_color":"(r,g,b)"}`
 * (discovery §6 W10), which is `dashboardApi`'s already-ported
 * `useUpdateUserMutation` (`dashboardApi.ts:69-88`) hitting the exact same URL
 * with the exact same verb. Re-declaring it here would create a SECOND cache
 * entry for the same resource inside a different `createApi` instance and let
 * the header's user go stale. `ColorTab.tsx` reuses the existing hook.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ STILL NO TAG TYPES AFTER U-b/U-c — AND THAT IS DELIBERATE. ★★
 *
 * U-a's note said "the first wave that needs cache tags should convert the
 * plain `injectEndpoints` below into `enhanceEndpoints({addTagTypes})`". U-b and
 * U-c are not that wave, because **this page never re-fetches anything after a
 * write** — discovery §6 says so on every single path:
 *   W1 create UDF   → "**No re-fetch.**" (the model is `unshift`ed in memory)
 *   W2 rename       → "**No re-fetch, no re-render.**"
 *   W3 content      → same PATCH-and-forget
 *   W5 PDF upload   → raw `fetch`, the Backbone model is never even updated
 *   W6 delete UDF   → "**No re-fetch of either collection.**"
 * A `providesTags`/`invalidatesTags` pair would put a GET on the wire that the
 * legacy page does not issue, on the one screen whose list is 283 rows — i.e. a
 * visible behaviour change on every create and every delete. `partnersApi.ts`
 * reached the same conclusion for the same reason (see its WRITES banner).
 *
 * What the writes do instead — exactly what Backbone does — is patch the
 * **client-side collection**. `SubmittalTab.tsx` owns that collection (it is the
 * literal port of `self.collectionUserDefinedFields`) and mirrors each edit back
 * into the RTK Query cache with `userSettingsApi.util.updateQueryData`, so that
 * an SPA remount of the route does not resurrect a deleted row. Zero network.
 *
 * `report/colors/`, the master lists and the CMS terms are read-only on this
 * page anyway (discovery §6 "Write paths that DO NOT exist"), so nothing could
 * invalidate them either. U-d/U-e should re-run this reasoning rather than
 * assume tags are needed: `submittal/format/` is also written without a
 * re-fetch (W7/W8/W9), with ONE exception — the `submittal:format:changed`
 * dispatcher event, which does re-`fetch()` the format collection and is U-d's
 * to port.
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ U-d LANDED, AND IT DID NOT NEED TAGS EITHER. ★★
 *
 * U-b/U-c's banner asked U-d to re-run the reasoning rather than assume. Done,
 * and the answer is still no:
 *   W7 reorder        → "**No re-fetch, no re-render**" (the collection is
 *                        patched in memory from the response echo)
 *   W8 drop-to-create → the ONE path with a re-fetch hook … which never fires.
 *                        `ModelSubmittalFormat.onchange` triggers
 *                        `Dispatcher 'submittal:format:changed'` →
 *                        `fetch_submittal_format` (`submittal.js:692-702`), and
 *                        that function re-fetches ONLY when
 *                        `self.last_action !== 'create_new_field'`. The single
 *                        place the dispatcher event can originate is
 *                        `createNewField` (`:797`), which sets exactly that
 *                        value one line before the `set()` that fires it — and
 *                        `updateSubmittalFormat`, the only other `set()` on a
 *                        format model, is dead code (§10). So the re-fetch
 *                        branch is **unreachable** and the observable effect of
 *                        the dispatcher is a full client-side `render()`.
 *   W9 delete row     → "**No re-fetch.**"
 * A `providesTags`/`invalidatesTags` pair on `getUserSettingsSubmittalFormat`
 * would therefore put a GET on the wire after every drop and every delete that
 * legacy does not issue. Still none.
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ U-e LANDED — THE `prp/…` FAMILY, AND STILL NO TAGS. ★★
 *
 * The seam left by U-d asked U-e to add the `prp/` mirror here and to re-run the
 * tag reasoning rather than assume. Done, and the answer is no for a THIRD time,
 * but for a subtly different reason than submittal's:
 *
 * Proposal re-renders far MORE than submittal does — `proposal.js:96-101` wires
 * `collectionUserDefinedFields 'change' → render` and `collectionPrpFormat
 * 'change' → render` with **no `name_change` guard** (submittal's listener,
 * `submittal.js:119-123`, has one; submittal's format listener is commented out
 * entirely). So a proposal UDF save or a format-model `set` rebuilds the whole
 * pane. But every one of those renders is CLIENT-SIDE, off the collections —
 * still zero GETs:
 *   create UDF  → `POST prp/user_defined_fields/ {}`, the model is `unshift`ed
 *                 and the response's `set` fires the render. No re-fetch.
 *   rename/body → `PATCH prp/user_defined_fields/<id>`; the render comes from
 *                 the same `set`. No re-fetch.
 *   delete UDF  → `DELETE …/<id>`; the collections are patched by hand.
 *   W7/W8/W9    → identical to submittal, and `fetch_proposal_format`
 *                 (`proposal.js:494-503`) early-returns on
 *                 `last_action === 'create_new_field'` — which `createNewField`
 *                 sets one line before the `set()` that reaches it, exactly as
 *                 submittal does. Unreachable re-fetch, same as W8's.
 * Tags would put GETs on the wire that legacy never issues. Still none.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** `submittal/model/user.defined.field.js:6` `urlRoot`. Backbone's default
 * `url()` is `urlRoot + id` when `urlRoot` already ends in `/`, which is why
 * every detail-route request below has **no trailing slash** (§6 W2/W6) while
 * the list/create route does. The DRF detail routes are
 * `…/(?P<pk>[0-9]+)$` (`rest_user_settings/urls.py:59-63`), so the distinction
 * is load-bearing, not cosmetic. */
const SUBMITTAL_UDF_URL = '/api/v1.0/user_settings/submittal/user_defined_fields/';

/** `submittal/model/submittal.format.js:14` `urlRoot` and
 * `submittal/collection/submittal.format.js:8` `url` — the same literal. Same
 * trailing-slash rule as above: the list/create route keeps it, the detail
 * routes (`urls.py:49-53`, `…/(?P<pk>[0-9]+)$`) do not. */
const SUBMITTAL_FORMAT_URL = '/api/v1.0/user_settings/submittal/format/';

/** U-e — `proposal/model/user.defined.field.js:6` `urlRoot` and
 * `proposal/collection/user.defined.fields.js:10` `url`. Same trailing-slash
 * rule as the submittal pair above: list/create keeps it, the detail routes
 * (`rest_user_settings/urls.py:31-36`, `…/(?P<pk>[0-9]+)$`) do not — verified on
 * the wire at §6 W6, `DELETE …/prp/user_defined_fields/50`. */
const PRP_UDF_URL = '/api/v1.0/user_settings/prp/user_defined_fields/';

/** U-e — `proposal/model/prp.format.js:12` `urlRoot` and
 * `proposal/collection/user.defined.prp.format.js:7` `url`. */
const PRP_FORMAT_URL = '/api/v1.0/user_settings/prp/format/';

/**
 * One row of `GET /api/v1.0/user_settings/report/colors/` — 139 of them on
 * dev3, ordered server-side by `order_id` (`viewset.py:586-599`,
 * `serializers.py:138-142`; discovery §7.3).
 *
 * `value` is the RGB string in the SAME shape as `KsdmUser.report_color`, i.e.
 * `"(21,11,132)"` — parentheses included, no spaces. That literal is what the
 * template concatenates onto `rgb` for the swatch background and what the
 * write path PATCHes back, so it is never parsed into numbers on the way out.
 *
 * `hexcode` is ALL LOWER-CASE (`"#150b84"`). ★ Note the field name: the legacy
 * template reads `{{hexCode}}` (camelCase) for its `data-hex` attribute, which
 * does not exist on the payload — that is quirk Q9 and the attribute is empty
 * on all 139 rows. The visible Hex column uses the correct `{{hexcode}}`.
 */
export interface IUserSettingsReportColor {
  id: number;
  name: string;
  value: string;
  order_id: number;
  hexcode: string;
}

/**
 * One row of `GET /api/v1.0/user_settings/submittal/user_defined_fields/` — the
 * **User** library (283 rows on dev3). `SubmittalUserDefinedFieldSerializer`
 * (`api/rest_user_settings/serializers.py:82-94`).
 *
 * ★ `field_order` is `read_only` and server-assigned (`models.py:113-119`,
 * `max+1`). It is NOT a display order — the list endpoint overrides Meta and
 * sorts `.order_by('name')` (`viewset.py:311-317`) — but it IS the key the
 * library `<li>` id is built from (`submittal.tmpl:60`
 * `id="submittal_udf_item_field_{{field_order}}"`), which is why the create flow
 * looks the new row up by `field_order` and not by `id`.
 *
 * ★ `udf_type_id` is a `CharField` on the wire ("1"/"2") even though the popup's
 * default sends the NUMBER 2 (`submittal.js:287` `self.type_format = 2`).
 *
 * ★ `pdf_file` is an absolute URL when set, `null` otherwise.
 *
 * ⚠ `list` is NETWORK-WIDE for a network user (`filter(user__access_type__value=
 * 'network')`, discovery §12 R6) — these rows are not necessarily the current
 * user's, and `field_order` is therefore only unique *per user*, so the
 * `submittal_udf_item_field_<field_order>` ids can collide. Legacy resolves such
 * a collision with `querySelector` (first match wins); the port does the same.
 */
export interface IUserSettingsSubmittalUdf {
  id: number;
  user_id: number;
  name: string | null;
  content: string | null;
  field_order: number;
  pdf_file: string | null;
  udf_type_name: string | null;
  udf_type_value: string | null;
  udf_type_id: string | null;
  checkbox_start_from_new_page: boolean;
}

/**
 * One row of `GET /api/v1.0/user_settings/submittal/format/` — the ordered
 * table of contents (18 rows on dev3), `ordering = ['order_id']`.
 *
 * `name` is a **Python property** (`user_settings/models.py:187-198`), resolved
 * either from `MasterAHUReport` (for the six CMS terms values) or from the
 * generic `content_object`; it is `null` for a freshly-created placeholder row.
 * `model` is the content type's lower-case model name and is the discriminator
 * the view maps back onto a library type (`submittal.js:114-118`).
 */
export interface IUserSettingsSubmittalFormatRow {
  id: number;
  user_id: number;
  object_id: number | null;
  content_type_id: number | null;
  order_id: number;
  cms_field_name: string | null;
  model: string | null;
  name: string | null;
  /** `content_object.value`; absent on rows with no content object. */
  value?: string | null;
  report_type: string | null;
}

/**
 * One row of `GET /api/v1.0/master/v1.0.0/ahu/user_settings/submittal_predefined_fields/`
 * — the **Application** library (20 rows). `value` is the engine section slug
 * and doubles as the detail pane's DOM id (`#submittal_<value>`), which is why
 * the two "Center of Gravity Calc" master rows produce a duplicate id (Q14).
 */
export interface IUserSettingsPredefinedField {
  id: number;
  name: string;
  value: string;
  report_type: string | null;
}

/**
 * `GET /api/v1.0/master/v1.0.0/ahu/user_defined_field_options/` — exactly two
 * rows: `{id:1, name:'Text Document', value:'text'}` and
 * `{id:2, name:'Portable Document Format (PDF)', value:'pdf'}`. They populate
 * the "Select type of User Defined Field" popup's radio list.
 */
export interface IUserSettingsUdfTypeOption {
  id: number;
  name: string;
  value: string;
}

/**
 * `GET /api/v1.0/cms/1/v1.0.0/ahu/terms/` — ONE object, ~219 KB, six HTML
 * bodies plus six display labels (`CMSAHUTermsSerializer`,
 * `api/rest_cms/serializers.py:4492-4514`).
 *
 * ★ THE `1` IS HARD-CODED IN LEGACY, not the user's mfg:
 * `mvc/model/cms.terms.js:6` is a literal `url: '/api/v1.0/cms/1/v1.0.0/ahu/terms/'`.
 * The backend read path scopes to `request.user.mfg` regardless of the path
 * segment, so for a non-mfg-1 user the URL is a lie that happens to work. Ported
 * literally — "fixing" it to `user.mfg_id` would change the URL on the wire.
 *
 * The submittal view derives its **CMS** library (6 rows) from this object by
 * walking `Object.keys` and keeping every key `k` for which `k + '_name'` is
 * truthy (`submittal.js:137-146`) — so the row set and its ORDER come from the
 * serializer's field order, not from a list endpoint.
 */
export interface IUserSettingsCmsTerms {
  id: number;
  mfg_id: number;
  sales_terms: string | null;
  shipping_terms: string | null;
  warranty_terms: string | null;
  ahu_guide_specs: string | null;
  condensing_unit_guide_specs: string | null;
  packaged_unit_guide_specs: string | null;
  sales_terms_name: string | null;
  shipping_terms_name: string | null;
  warranty_terms_name: string | null;
  ahu_guide_specs_name: string | null;
  condensing_unit_guide_specs_name: string | null;
  packaged_unit_guide_specs_name: string | null;
}

/** The subset of `IUserSettingsSubmittalUdf` a PATCH may carry (§6 W2/W3/W4).
 * Deliberately NOT `Partial<IUserSettingsSubmittalUdf>`: the only keys legacy
 * ever puts in `self.data` are these three. */
export interface IUserSettingsSubmittalUdfPatch {
  name?: string;
  content?: string;
  checkbox_start_from_new_page?: boolean;
}

/**
 * One entry of the `/format/drag/` payload — §6 W7 and W8's second request.
 *
 * ★★ `id` IS A STRING ON ONE PATH AND A NUMBER ON THE OTHER, AND SO IS THE KEY
 * ORDER. That is not sloppiness in the type, it is the wire:
 *
 *   W7 (reorder)        `makeSortable`'s `stop` (`submittal.js:851-860`) builds
 *                       each entry as `{ order_id, id: row.id.replace("row-","") }`
 *                       — read off the DOM, so `order_id` FIRST and `id` a
 *                       **string**:  `{"order_id":0,"id":"126"}`.
 *   W8 (drop-to-create) `changeOrder` (`:764-780`) builds it from
 *                       `collection.toJSON()` as `{ id: element.id, order_id }`
 *                       — so `id` FIRST and a **number**:
 *                       `{"id":126,"order_id":0}`.
 *
 * `JSON.stringify` emits string keys in insertion order, so both call sites
 * construct their object literals in the legacy order and the bodies match
 * byte-for-byte. The RESPONSE always echoes ints (`{"id":126,"order_id":0}`),
 * which is why `IUserSettingsSubmittalOrderResponse` narrows `id` to `number`.
 */
export interface IUserSettingsSubmittalOrderEntry {
  id: number | string;
  order_id: number;
}

/** `UserSettingsOrderSerializer` echoes `validated_data`, NOT DB state
 * (`viewset.py:568-583`) — so this is the request coming back with `id` coerced
 * to an int, not a fresh read of the table. */
export interface IUserSettingsSubmittalOrderResponse {
  settings?: { id: number; order_id: number }[];
}

/**
 * The body of §6 W8's THIRD request, `PATCH …/submittal/format/<id>`.
 *
 * ★★ EVERY FIELD HERE IS OPTIONAL FOR A REASON, AND THE OMISSIONS ARE THE
 * BEHAVIOUR. `createNewField` (`submittal.js:798-802`) does
 *   `field.set({object_id: target_id, type: target_type, cms_field_name: target_name})`
 * with values read straight off the dragged anchor's attributes, so the two that
 * the anchor does not carry are `undefined`. Backbone's `set` still records them
 * as *changed* (`_.isEqual(null, undefined)` is false), `onchange` then saves
 * `self.changed` with `{patch:true}` — and `JSON.stringify` DROPS undefined
 * values. Hence, verbatim from the live capture (§6 W8):
 *   · CMS drop  → `{"type":"cms_terms","cms_field_name":"shipping_terms"}`
 *     ★ **no `object_id`** — the backend resolves it itself from
 *     `CMSAHUTerms.objects.get(mfg=request.user.mfg)`;
 *   · user drop → `{"object_id":"13","type":"user_defined"}` — a STRING id
 *     (`data-id` is an attribute), and no `cms_field_name`;
 *   · app drop  → `{"object_id":"<master id>","type":"app_defined"}`.
 * Build the literal in the declaration order below and let the undefineds fall
 * out; do not prune the keys by hand or the key ORDER changes.
 *
 * `type` is a **write-only discriminator** — the backend picks the content type
 * from it and never stores it.
 *
 * ★★ TWO LIVE 500s SIT BEHIND THIS ENDPOINT. Mirrored, never guarded:
 *  R7 — `UserSettingsSubmittalFormatViewSet.partial_update` (`viewset.py:506`)
 *       reads an undefined name: `if 'object_id' in request.data or
 *       changed_object_id:` → `NameError` → **500** whenever `object_id` is
 *       ABSENT and `type == 'user_defined'`. The UI never lands there because a
 *       user drop always carries `object_id` — but a CMS drop omits it, so the
 *       only thing keeping this off the wire is that `type` is `cms_terms`.
 *  R8 — both format viewsets read `request.data['type']` with **no `.get()`**,
 *       so a PATCH without `type` is a `KeyError` → **500**. `type` is
 *       therefore required in this interface, exactly as legacy always sends it.
 */
export interface IUserSettingsSubmittalFormatPatch {
  /** `data-id` off the dragged anchor. Absent for CMS (★ R7's trigger). */
  object_id?: string;
  /** `data-type || 'user_defined'` (`submittal.js:788`). Never omitted (★ R8). */
  type: SubmittalDragType;
  /** `data-name` off the dragged anchor — CMS only. */
  cms_field_name?: string;
}

/** The three `data-type` values the library anchors carry, with the user
 * anchors carrying NONE and falling back to `user_defined`. */
export type SubmittalDragType = 'user_defined' | 'cms_terms' | 'app_defined';

// ═══════════════════════════════════════════════════════════════════════════
// U-e — THE PROPOSAL (`prp/…`) TYPES.
//
// ★ DELIBERATELY NOT ALIASES OF THE SUBMITTAL ONES. They are close, and every
// place they differ is load-bearing: the PRP UDF has no `pdf_file`, no
// `udf_type_*` and no `checkbox_start_from_new_page` (the Proposal tab has no
// PDF path at all — §4.2, and `proposal.tmpl` renders neither an upload form nor
// an iframe); the PRP format row has no `report_type`; and the PRP predefined
// field has no `report_type` either (§7.3). Declaring them separately keeps a
// future edit to one tab from silently widening the other.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * One row of `GET /api/v1.0/user_settings/prp/user_defined_fields/` — the
 * **User** library of the Proposal tab (6 rows on dev3: Introduction, Validity,
 * Lead times, Price adds, Tax terms, Signature). `PRPUserDefinedFieldSerializer`
 * (`api/rest_user_settings/serializers.py:16-25`).
 *
 * ★ FIVE KEYS, AND NO MORE. There is no `pdf_file`, so there is no upload path,
 * no `<iframe>`, no `validatePDF`, no 30 MB gate and no Q15.
 *
 * ★ `field_order` is server-assigned and `read_only`, the list endpoint sorts by
 * `name`, and — exactly as on submittal — it is the key the library `<li>` id
 * and the DETAIL PANE id are built from (`proposal.tmpl:73,181`
 * `proposal_udf_item_field_{{field_order}}` /
 * `proposal_user_defined_field_{{field_order}}`), while the pane's CLASS is
 * built from `id` (`proposal_user_defined_field_{{id}}`). Both keys are in play
 * on this tab and they are not interchangeable.
 *
 * ⚠ `list` is NETWORK-WIDE for a network user (§12 R6), same as submittal.
 */
export interface IUserSettingsPrpUdf {
  id: number;
  user_id: number;
  name: string | null;
  content: string | null;
  field_order: number;
}

/**
 * One row of `GET /api/v1.0/user_settings/prp/format/` — the Proposal format
 * rail (9 rows on dev3), `ordering = ['order_id']`.
 *
 * Same shape as the submittal format row **minus `report_type`** (§7.3), with
 * `value` present only on app-defined rows. `model` is the discriminator
 * `proposal.js:91-95` maps onto a library type, and its user/app members differ
 * from submittal's: `prpuserdefinedfield` and `masterproposalpredefinedfield`.
 */
export interface IUserSettingsPrpFormatRow {
  id: number;
  user_id: number;
  object_id: number | null;
  content_type_id: number | null;
  order_id: number;
  cms_field_name: string | null;
  model: string | null;
  name: string | null;
  /** `content_object.value`; absent on rows with no content object. */
  value?: string | null;
}

/**
 * One row of
 * `GET /api/v1.0/master/v1.0.0/ahu/user_settings/prp_predefined_fields/` — the
 * **Application** library of the Proposal tab. THREE rows: Equipment List,
 * Price Summary, Equipment Description (vs submittal's 20).
 *
 * ★ `value` doubles as the detail pane's DOM id — and unlike submittal, it is
 * used RAW (`proposal.tmpl:217` `id="{{value}}"`, not `id="submittal_{{value}}"`).
 * So the Proposal tab really does own `#equipment_list`, `#price_summary` and
 * `#equipment_description` at document level.
 */
export interface IUserSettingsPrpPredefinedField {
  id: number;
  name: string;
  value: string;
}

/**
 * The subset of `IUserSettingsPrpUdf` a PATCH may carry.
 *
 * ★ TWO KEYS, NOT THREE. `self.data` on the proposal view is only ever written
 * by `updateUserDefinedField` (`proposal.js:245-259`), which branches on the
 * blurred element's `name` attribute and knows exactly `'name'` and `'content'`.
 * There is no "start from new page" checkbox on this tab, so Q5 — submittal's
 * data-loss quirk — has no counterpart here.
 */
export interface IUserSettingsPrpUdfPatch {
  name?: string;
  content?: string;
}

/**
 * One entry of `PATCH …/prp/format/drag/`.
 *
 * ★★ THE SAME TWO SHAPES AS SUBMITTAL, FROM THE SAME TWO CALL SITES, AND FOR
 * THE SAME REASON — see `IUserSettingsSubmittalOrderEntry`:
 *   W7 (reorder)        `makeSortable`'s `stop` (`proposal.js:625-634`) reads the
 *                       DOM: `{ order_id, id: row.id.replace("row-","") }` —
 *                       `order_id` first, `id` a **string**.
 *   W8 (drop-to-create) `changeOrder` (`proposal.js:535-551`) reads
 *                       `collection.toJSON()`: `{ id, order_id }` — `id` first
 *                       and a **number**.
 * Both literals are constructed in that key order so `JSON.stringify` matches
 * the captured bodies byte-for-byte.
 */
export interface IUserSettingsPrpOrderEntry {
  id: number | string;
  order_id: number;
}

/** `UserSettingsOrderSerializer` echoes `validated_data`, not DB state. */
export interface IUserSettingsPrpOrderResponse {
  settings?: { id: number; order_id: number }[];
}

/**
 * The body of §6 W8's third request on the Proposal tab,
 * `PATCH …/prp/format/<id>`.
 *
 * Identical construction to the submittal one — `createNewField`
 * (`proposal.js:572-576`) `set`s `{object_id, type, cms_field_name}` from the
 * dragged anchor's attributes, Backbone saves `self.changed` with `{patch:true}`
 * and `JSON.stringify` drops the `undefined` members — so the same three bodies
 * go out, one per library source. §6 W8's captured **user** example is a
 * PROPOSAL one: `{"object_id":"13","type":"user_defined"}`.
 *
 * ★★ THE SAME TWO 500s (R7/R8) SIT BEHIND THIS ENDPOINT. Mirrored, never
 * guarded: `type` is always sent (R8 is a bare `request.data['type']`), and a
 * CMS drop deliberately omits `object_id` because the backend resolves it from
 * `CMSAHUTerms.objects.get(mfg=request.user.mfg)`.
 */
export interface IUserSettingsPrpFormatPatch {
  /** `data-id` off the dragged anchor. Absent for CMS (★ R7's trigger). */
  object_id?: string;
  /** `data-type || 'user_defined'` (`proposal.js:562`). Never omitted (★ R8). */
  type: SubmittalDragType;
  /** `data-name` off the dragged anchor — CMS only. */
  cms_field_name?: string;
}

export const userSettingsApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * `color/collection/colors.js` — `urlRoot`/`url` are both the literal
     * `/api/v1.0/user_settings/report/colors/` (trailing slash REQUIRED; the
     * list route is `re_path(r'^report/colors/$')`). No params, no ordering
     * applied client-side: the view renders `collection.toJSON()` in arrival
     * order, so neither do we.
     */
    getUserSettingsReportColors: builder.query<IUserSettingsReportColor[], void>({
      query: () => '/api/v1.0/user_settings/report/colors/',
    }),

    // =====================================================================
    // U-b — THE SUBMITTAL VIEW'S FIVE BOOT FETCHES.
    //
    // `submittal.js:130-136` fires all five as ONE `Promise.all` and renders
    // nothing until every one resolves. `SubmittalTab.tsx` subscribes to all
    // five and gates its first render on the same conjunction, which reproduces
    // both the parallelism and the all-or-nothing paint.
    // =====================================================================

    /** `submittal/collection/app.defined.fields.js:6`. The Application library
     * (20 rows). The viewset `.exclude(value__in=['er_wheel_selection'])`, which
     * is why the template's `er_wheel_selection` branch is dead (§10). */
    getUserSettingsSubmittalPredefinedFields: builder.query<IUserSettingsPredefinedField[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/user_settings/submittal_predefined_fields/',
    }),

    /** `submittal/collection/user.defined.fields.js:8`. The User library (283
     * rows), server-sorted by `name`. */
    getUserSettingsSubmittalUserDefinedFields: builder.query<IUserSettingsSubmittalUdf[], void>({
      query: () => SUBMITTAL_UDF_URL,
    }),

    /** `submittal/collection/submittal.format.js:6`. The format rail (18 rows),
     * server-sorted by `order_id`. */
    getUserSettingsSubmittalFormat: builder.query<IUserSettingsSubmittalFormatRow[], void>({
      query: () => '/api/v1.0/user_settings/submittal/format/',
    }),

    /** `submittal/collection/user.defined.field.type.format.js:6`. */
    getUserSettingsUdfTypeOptions: builder.query<IUserSettingsUdfTypeOption[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/user_defined_field_options/',
    }),

    /**
     * `model/cms.terms.js:6` — GET only.
     *
     * ⚠ ACCEPTED BEHAVIOUR CHANGE (discovery §12 "Port-specific risks"): legacy
     * fetches this 219 KB payload **TWICE**, once per view, because the submittal
     * and proposal views each `new ModelCMSTerms()`. RTK Query serves both
     * subscribers from one cache entry, so React issues it ONCE. Deliberately
     * NOT contorted back into two requests — the payload is identical, nothing
     * on the page depends on there being two, and the only observable difference
     * is 219 KB less traffic. Flagged here so a request-counting verifier reads
     * 14 boot requests instead of 15 and knows why.
     *
     * ⚠ The legacy model also binds `change → save` (`cms.terms.js:16-18`), but
     * the CMS panes render `<input readonly>` and a plain `<div>` with no
     * `contenteditable` (`submittal.tmpl:294-306`), so no write is reachable and
     * none is declared here (discovery §6 "Write paths that DO NOT exist").
     */
    getUserSettingsCmsTerms: builder.query<IUserSettingsCmsTerms, void>({
      query: () => '/api/v1.0/cms/1/v1.0.0/ahu/terms/',
    }),

    // =====================================================================
    // U-c — THE SUBMITTAL UDF LIFECYCLE.
    //
    // ⚠ NO `invalidatesTags` ANYWHERE — see the banner at the top of the file.
    // Legacy re-fetches nothing after any of these; `SubmittalTab.tsx` patches
    // its own collection and mirrors the edit into the cache by hand.
    //
    // ★★ TWO LIVE BACKEND LANDMINES sit behind these three endpoints. They are
    // NOT worked around here, and no client-side guard is added, because the
    // brief is parity-only and the client cannot fix either one:
    //
    //  R4 — `SubmittalUserDefinedFieldViewSet.destroy`
    //       (`api/rest_user_settings/viewset.py:429`) runs
    //       `UserSettingsSubmittalFormat.objects.filter(object_id=field.pk)
    //       .delete()` with **no `content_type_id` and no `user_id`**. Deleting
    //       UDF #17 therefore also deletes every format row whose `object_id`
    //       happens to be 17 — including rows pointing at a
    //       `MasterSubmittalPredefinedField` and rows belonging to OTHER USERS.
    //       (The client mirrors the same object_id-only filter locally, because
    //       that is what legacy does — `submittal.js:506-513`.)
    //  R5 — `update_storage` (`viewset.py:288-301`) has its `user_id` kwarg
    //       commented out, so ONE `UserMasterMfgLibrarySettings` row is shared
    //       by every user, and it recomputes `used_storage` by `os.walk`-ing
    //       `$KSDM_MEDIA_ROOT/subbmital_package_pdf` on EVERY UDF PATCH and
    //       DELETE. On a box whose media directory is not synced (dev3), any
    //       submittal-UDF write silently rewrites the shared quota to 0.
    //       DISCOVER hit this and restored it by hand (§14).
    // =====================================================================

    /**
     * §6 W1 — `POST /api/v1.0/user_settings/submittal/user_defined_fields/`
     * with the single key `{"udf_type_id": <1|2>}` and NOTHING else.
     *
     * ★ THE VALUE'S TYPE IS NOT STABLE, AND THAT IS PARITY.
     * `openUserDefinedFieldPopUp` (`submittal.js:285-293`) seeds
     * `self.type_format = 2` — the NUMBER 2, matching the template's `checked`
     * PDF radio. `changeUDFTypeFormat` (`:294-297`) then replaces it with
     * `e.target.dataset.id`, a STRING. So an untouched popup sends `{"udf_type_id":2}`
     * and a touched one sends `{"udf_type_id":"1"}` / `{"udf_type_id":"2"}`.
     * The serializer declares `udf_type_id` as a `CharField`, so both are
     * accepted; the union below carries the distinction to the wire.
     *
     * The server assigns `name` ('UDF Name', or a `MasterUserDefinedFieldDefaultText`
     * row matched on `order_id`) and `field_order = max+1` (`models.py:112-129`).
     */
    createUserSettingsSubmittalUdf: builder.mutation<
      IUserSettingsSubmittalUdf,
      { udf_type_id: number | string }
    >({
      query: (body) => ({ url: SUBMITTAL_UDF_URL, method: 'POST', body }),
    }),

    /**
     * §6 W2/W3/W4 — `PATCH …/user_defined_fields/<id>` (no trailing slash).
     *
     * The body is `self.data` verbatim: whatever keys accumulated in it since
     * the last successful save, which is exactly one, two or three of
     * `name` / `content` / `checkbox_start_from_new_page`. Never a full object.
     */
    patchUserSettingsSubmittalUdf: builder.mutation<
      IUserSettingsSubmittalUdf,
      { id: number; body: IUserSettingsSubmittalUdfPatch }
    >({
      query: ({ id, body }) => ({ url: `${SUBMITTAL_UDF_URL}${id}`, method: 'PATCH', body }),
    }),

    /**
     * §6 W6 — `DELETE …/user_defined_fields/<id>` → 204, no trailing slash.
     * Backbone's `destroy()` is optimistic (no `{wait:true}`): the model leaves
     * the collection before the response, and a failure is never reverted.
     */
    deleteUserSettingsSubmittalUdf: builder.mutation<void, number>({
      query: (id) => ({ url: `${SUBMITTAL_UDF_URL}${id}`, method: 'DELETE' }),
    }),

    // =====================================================================
    // U-d — THE FORMAT RAIL'S THREE WRITE PATHS (§6 W7 / W8 / W9).
    //
    // ⚠ NO `invalidatesTags` here either — see the U-d banner at the top.
    // =====================================================================

    /**
     * §6 W8, request 1 of 3 — `POST …/submittal/format/` with an **empty body**.
     *
     * `addStructureField` (`submittal.js:665-671`) `save()`s a brand-new
     * `ModelSubmittalFormat` that has no attributes at all, so Backbone sends
     * the literal `{}`. The server answers with a fully-null placeholder row
     * parked at `max(order_id)+1`:
     *   `{"id":259,"user_id":2,"object_id":null,"content_type_id":null,
     *     "order_id":18,"cms_field_name":null,"model":null,"name":null,
     *     "report_type":null}`
     * It is a real, persisted, contentless row for the two requests it takes to
     * become a real one — and if either of those fails it simply stays that way
     * (a blank rail row), which is the legacy behaviour.
     */
    createUserSettingsSubmittalFormat: builder.mutation<IUserSettingsSubmittalFormatRow, void>({
      query: () => ({ url: SUBMITTAL_FORMAT_URL, method: 'POST', body: {} }),
    }),

    /**
     * §6 W7, and §6 W8's request 2 of 3 — `PATCH …/submittal/format/drag/`
     * carrying the FULL ordered list under `settings`.
     *
     * ★ WHY THIS IS A `queryFn` AND NOT A ONE-LINE `query`.
     * Legacy does not reach this endpoint through Backbone: `updateOrdering`
     * (`submittal.js:744-763`) calls `fn.uni_tools.patch`
     * (`static/mvc/universal_fn/universal_fn_tools.js:177-207`), which **retries
     * up to 3 times, but only on a 500**, and throws on any other non-2xx. Two
     * requests where React would send one is a wire difference a
     * request-counting verifier can see, so the loop is reproduced literally.
     * (`standardizeURL` is a no-op here — the URL already ends in `/`. The
     * headers `fn.uni_tools.patch` sets — `Content-type: application/json`,
     * `X-CSRFToken`, `X-Requested-With: XMLHttpRequest` — are exactly what
     * `apiConfig.ts`'s `baseQuery` already sends.)
     *
     * On failure the promise REJECTS, and both call sites depend on that:
     * `changeOrder` `await`s it, so a failed reorder inside a drop-to-create
     * aborts before the third request ever goes out.
     */
    dragUserSettingsSubmittalFormat: builder.mutation<
      IUserSettingsSubmittalOrderResponse,
      { settings: IUserSettingsSubmittalOrderEntry[] }
    >({
      async queryFn(body, _api, _extraOptions, fetchWithBQ) {
        for (let attempt = 0; ; attempt += 1) {
          const result = await fetchWithBQ({
            url: `${SUBMITTAL_FORMAT_URL}drag/`,
            method: 'PATCH',
            body,
          });
          if (!result.error) {
            return { data: (result.data ?? {}) as IUserSettingsSubmittalOrderResponse };
          }
          // `if (resp.status == 500 && i < retries - 1) continue;` — anything
          // else (or the third 500) throws.
          if (result.error.status === 500 && attempt < 2) continue;
          return { error: result.error };
        }
      },
    }),

    /**
     * §6 W8, request 3 of 3 — `PATCH …/submittal/format/<id>` (no trailing
     * slash). See `IUserSettingsSubmittalFormatPatch` for what is and is not in
     * the body, and for the two 500s (R7/R8) this endpoint can raise.
     *
     * The response is the fully-resolved row — `name` and `model` computed
     * server-side, `object_id` coerced to an int, `content_type_id` filled in.
     * Legacy folds it back into the Backbone model (that is just `save()`'s
     * normal parse step) and it is that model, not the hand-injected DOM row,
     * that the subsequent full `render()` paints from.
     */
    patchUserSettingsSubmittalFormat: builder.mutation<
      IUserSettingsSubmittalFormatRow,
      { id: number; body: IUserSettingsSubmittalFormatPatch }
    >({
      query: ({ id, body }) => ({ url: `${SUBMITTAL_FORMAT_URL}${id}`, method: 'PATCH', body }),
    }),

    /**
     * §6 W9 — `DELETE …/submittal/format/<id>` → 204, no trailing slash.
     * Backbone's `destroy()` again, so again optimistic and never reverted.
     *
     * ⚠ R3: `UserSettingsSubmittalFormat` has no `delete()` override (unlike
     * `UserSettingsPRPStructure`, `models.py:74-78`), so the surviving rows keep
     * their old `order_id`s and the table is left with a hole. Harmless while
     * the client keeps PATCHing full orderings, visible after a reload. Not
     * compensated for here — the client re-indexes nothing on this path either.
     */
    deleteUserSettingsSubmittalFormat: builder.mutation<void, number>({
      query: (id) => ({ url: `${SUBMITTAL_FORMAT_URL}${id}`, method: 'DELETE' }),
    }),

    // =====================================================================
    // U-e — THE PROPOSAL TAB. Four boot reads + six writes.
    //
    // `proposal.js:112-117` fires FOUR fetches as one `Promise.all` — one fewer
    // than submittal, because there is no `user_defined_field_options` call:
    // the Proposal tab has no "select type of User Defined Field" popup, so
    // there is no type list to populate. `#add_user_defined_field` creates
    // immediately (§4.2, §5.3 #1).
    //
    // ⚠ NO `invalidatesTags` HERE EITHER — see the U-e banner at the top.
    //
    // ★★ THE SAME DESTRUCTIVE BACKEND PATHS AS SUBMITTAL, ONE OF THEM WORSE.
    // Mirrored, never guarded — the client cannot fix any of them:
    //  R4's PRP TWIN — `PRPUserDefinedFieldViewSet.destroy`
    //       (`api/rest_user_settings/viewset.py:122`) runs
    //       `UserSettingsPRPStructure.objects.filter(object_id=field.pk)
    //       .delete()` with **no `content_type_id` and no `user_id`**. Deleting
    //       PRP UDF #17 also deletes every PRP format row whose `object_id` is
    //       17 — across content types AND across users. The client mirrors the
    //       same `object_id`-only filter locally because that is what
    //       `proposal.js:316-325` does.
    //  R5 — `update_storage` zeroes the SHARED `UserMasterMfgLibrarySettings`
    //       quota row (its `user_id` kwarg is commented out) by re-walking the
    //       media directory on UDF writes.
    //  R7/R8 — the format viewset's undefined `changed_object_id` and its
    //       unguarded `request.data['type']`, both → 500. See
    //       `IUserSettingsPrpFormatPatch`.
    // =====================================================================

    /** `proposal/collection/app.defined.fields.js:7`. The Application library —
     * THREE rows (§7.3). */
    getUserSettingsPrpPredefinedFields: builder.query<IUserSettingsPrpPredefinedField[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/user_settings/prp_predefined_fields/',
    }),

    /** `proposal/collection/user.defined.fields.js:10`. The User library — SIX
     * rows, server-sorted by `name`. */
    getUserSettingsPrpUserDefinedFields: builder.query<IUserSettingsPrpUdf[], void>({
      query: () => PRP_UDF_URL,
    }),

    /** `proposal/collection/user.defined.prp.format.js:7`. The format rail —
     * NINE rows, server-sorted by `order_id`. */
    getUserSettingsPrpFormat: builder.query<IUserSettingsPrpFormatRow[], void>({
      query: () => PRP_FORMAT_URL,
    }),

    /**
     * §6 W1's PROPOSAL counterpart — and it is NOT the same request.
     *
     * `addUserDefinedField` (`proposal.js:217-238`) does `new
     * ModelUserDefinedField()` followed by a bare `field.save()`. The model has
     * no attributes, so `toJSON()` is `{}` and Backbone POSTs the **empty
     * object** — whereas submittal seeds `{udf_type_id: <1|2>}` from its type
     * popup. There is no popup on this tab and no `udf_type` column on
     * `PRPUserDefinedField` at all.
     *
     * The server assigns `name` and `field_order = max+1`
     * (`user_settings/models.py:17-52`).
     */
    createUserSettingsPrpUdf: builder.mutation<IUserSettingsPrpUdf, void>({
      query: () => ({ url: PRP_UDF_URL, method: 'POST', body: {} }),
    }),

    /**
     * §6 W2/W3's counterpart — `PATCH …/prp/user_defined_fields/<id>`, no
     * trailing slash. The body is `self.data` verbatim: `name`, `content`, or
     * both. Never `checkbox_start_from_new_page` — that column does not exist
     * on this model.
     */
    patchUserSettingsPrpUdf: builder.mutation<
      IUserSettingsPrpUdf,
      { id: number; body: IUserSettingsPrpUdfPatch }
    >({
      query: ({ id, body }) => ({ url: `${PRP_UDF_URL}${id}`, method: 'PATCH', body }),
    }),

    /** §6 W6 — `DELETE …/prp/user_defined_fields/<id>` → 204 (captured live on
     * id 50). Backbone's optimistic `destroy()`; never reverted. ★★ R4's twin
     * fires server-side. */
    deleteUserSettingsPrpUdf: builder.mutation<void, number>({
      query: (id) => ({ url: `${PRP_UDF_URL}${id}`, method: 'DELETE' }),
    }),

    /** §6 W8, request 1 of 3 — `POST …/prp/format/` with an empty body
     * (`addStructureField`, `proposal.js:487-493`). Returns a fully-null
     * placeholder row parked at `max(order_id)+1`. */
    createUserSettingsPrpFormat: builder.mutation<IUserSettingsPrpFormatRow, void>({
      query: () => ({ url: PRP_FORMAT_URL, method: 'POST', body: {} }),
    }),

    /**
     * §6 W7, and §6 W8's request 2 of 3 — `PATCH …/prp/format/drag/` carrying
     * the FULL ordered list under `settings`. §6 W7: "Proposal is identical at
     * `/api/v1.0/user_settings/prp/format/drag/`."
     *
     * The retry loop is `fn.uni_tools.patch`
     * (`static/mvc/universal_fn/universal_fn_tools.js:177-207`) again — up to 3
     * attempts, but ONLY on a 500 — because `updateOrdering`
     * (`proposal.js:515-534`) reaches the endpoint through exactly the same
     * helper, not through Backbone. Two requests where React sends one is a wire
     * difference a request-counting verifier can see, so the loop is transcribed
     * rather than shared with the submittal endpoint above.
     *
     * On failure the promise REJECTS, and `changeOrder` `await`s it — so a
     * failed reorder inside a drop-to-create aborts before the third request.
     */
    dragUserSettingsPrpFormat: builder.mutation<
      IUserSettingsPrpOrderResponse,
      { settings: IUserSettingsPrpOrderEntry[] }
    >({
      async queryFn(body, _api, _extraOptions, fetchWithBQ) {
        for (let attempt = 0; ; attempt += 1) {
          const result = await fetchWithBQ({
            url: `${PRP_FORMAT_URL}drag/`,
            method: 'PATCH',
            body,
          });
          if (!result.error) {
            return { data: (result.data ?? {}) as IUserSettingsPrpOrderResponse };
          }
          if (result.error.status === 500 && attempt < 2) continue;
          return { error: result.error };
        }
      },
    }),

    /** §6 W8, request 3 of 3 — `PATCH …/prp/format/<id>` (no trailing slash).
     * See `IUserSettingsPrpFormatPatch` for the three bodies and the two 500s. */
    patchUserSettingsPrpFormat: builder.mutation<
      IUserSettingsPrpFormatRow,
      { id: number; body: IUserSettingsPrpFormatPatch }
    >({
      query: ({ id, body }) => ({ url: `${PRP_FORMAT_URL}${id}`, method: 'PATCH', body }),
    }),

    /**
     * §6 W9 — `DELETE …/prp/format/<id>` → 204 (captured live on id 36).
     *
     * ★★ THIS IS WHERE PRP AND SUBMITTAL GENUINELY DIVERGE ON THE SERVER (R3).
     * `UserSettingsPRPStructure` OVERRIDES `delete()` and re-indexes every
     * surviving row's `order_id` (`user_settings/models.py:74-78`);
     * `UserSettingsSubmittalFormat` has no such override and leaves a hole.
     *
     * ★ The CLIENT still does not re-index on this path — `removePRPStructureField`
     * (`proposal.js:504-514`) removes the row and renumbers `data-index` only,
     * exactly like submittal. So do NOT "help" the server by re-numbering
     * `order_id` here: the next ordering PATCH is what the client believes, and
     * inventing a re-index would put a different payload on the wire. The
     * observable difference is only after a reload, and it belongs to the
     * server.
     */
    deleteUserSettingsPrpFormat: builder.mutation<void, number>({
      query: (id) => ({ url: `${PRP_FORMAT_URL}${id}`, method: 'DELETE' }),
    }),
  }),
});

// ═══════════════════════════════════════════════════════════════════════════
// §6 W5 — THE PDF CUT-SHEET UPLOAD. The one request on this page that does not
// go through RTK Query, in legacy OR here.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `submittal.js:324-362 uploadPDF` — a raw `fetch` with a `multipart/form-data`
 * body, built by handing the whole `<form id="pdf_upload_<id>">` to `FormData`.
 *
 * ★ WHY IT IS NOT AN RTK QUERY MUTATION.
 * `fetchBaseQuery` is perfectly happy to send a `FormData` body (it skips
 * `JSON.stringify` and leaves `Content-Type` unset so the browser can add the
 * multipart boundary), so the mechanical objection is not the problem. The
 * problem is the RESULT: a mutation's job is to feed the cache, and this write
 * deliberately feeds NOTHING. Legacy's `uploadPDF` bypasses Backbone entirely,
 * so `collectionUserDefinedFields.get(id)` keeps `pdf_file: null` even after a
 * successful upload, and the next full `render()` therefore rebuilds the pane
 * with an EMPTY `data-placeholder` and the freshly-uploaded PDF vanishes from
 * the viewer until the page is reloaded. That is the behaviour to mirror
 * (`SubmittalTab.tsx` keeps the returned URL in per-pane view state only, and
 * never writes it back to the collection or the cache). Declaring a mutation
 * would invite exactly the cache write that breaks it.
 *
 * ★ CSRF / 401 / credentials — matched to `apiConfig.ts:13-43` on purpose,
 * because going around `baseQuery` means going around the place those three
 * concerns are handled centrally:
 *   · `X-CSRFToken` from the `csrftoken` cookie — legacy sets this too
 *     (`submittal.js:331,347`, via the GLOBAL `getCookie`; the view's own
 *     `getCookie` at `:317` is shadowed dead code, §10).
 *   · `credentials: 'same-origin'` — legacy omits the option and gets the same
 *     value from fetch's default; stated explicitly so it cannot drift.
 *   · `401 → window.location.href = '/login/'` — `apiConfig.ts:38-40`. THIS ONE
 *     IS A DELIBERATE DEVIATION: legacy would fall into `!response.ok` and print
 *     "Failed to upload PDF." inline instead of redirecting. A 401 here means
 *     the session died mid-page, and leaving the user on a dead page with a
 *     misleading inline error while every OTHER request on the page redirects
 *     would be worse than the inconsistency. Recorded in the wave's DEVIATIONS.
 *
 * ★ NOT sent: `X-Requested-With: XMLHttpRequest`. `apiConfig` adds it to every
 * request, but legacy's raw `fetch` does not send it, no Django view on this
 * path reads it, and it is neither CSRF nor 401 handling — so the wire stays
 * byte-identical to legacy.
 *
 * ★ NEVER set `Content-Type` here. The browser must generate the multipart
 * boundary; setting it by hand produces a body Django cannot parse.
 */
export async function uploadUserSettingsSubmittalPdf(
  id: number,
  form: HTMLFormElement
): Promise<IUserSettingsSubmittalUdf> {
  const token = getCookie('csrftoken');
  const response = await fetch(`${SUBMITTAL_UDF_URL}${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: token ? { 'X-CSRFToken': token } : {},
    body: new FormData(form),
  });
  if (response.status === 401) {
    window.location.href = '/login/';
    // Never settles — the navigation is already under way, and settling would
    // let the caller paint an error over a page that is leaving.
    return new Promise<IUserSettingsSubmittalUdf>(() => {});
  }
  // `submittal.js:351-354` verbatim: ANY non-2xx becomes this one string, which
  // `errorMSG` renders inline. Note the server's own 400 body for an
  // over-quota upload (`{"message": "Maximum library size exceeded."}`,
  // `viewset.py:396`) is therefore NEVER shown — the user sees the generic
  // message. Mirrored, not improved.
  if (!response.ok) throw new Error('Failed to upload PDF.');
  return (await response.json()) as IUserSettingsSubmittalUdf;
}

export const {
  useGetUserSettingsReportColorsQuery,
  useGetUserSettingsSubmittalPredefinedFieldsQuery,
  useGetUserSettingsSubmittalUserDefinedFieldsQuery,
  useGetUserSettingsSubmittalFormatQuery,
  useGetUserSettingsUdfTypeOptionsQuery,
  useGetUserSettingsCmsTermsQuery,
  useCreateUserSettingsSubmittalUdfMutation,
  usePatchUserSettingsSubmittalUdfMutation,
  useDeleteUserSettingsSubmittalUdfMutation,
  useCreateUserSettingsSubmittalFormatMutation,
  useDragUserSettingsSubmittalFormatMutation,
  usePatchUserSettingsSubmittalFormatMutation,
  useDeleteUserSettingsSubmittalFormatMutation,
  // U-e — the Proposal tab.
  useGetUserSettingsPrpPredefinedFieldsQuery,
  useGetUserSettingsPrpUserDefinedFieldsQuery,
  useGetUserSettingsPrpFormatQuery,
  useCreateUserSettingsPrpUdfMutation,
  usePatchUserSettingsPrpUdfMutation,
  useDeleteUserSettingsPrpUdfMutation,
  useCreateUserSettingsPrpFormatMutation,
  useDragUserSettingsPrpFormatMutation,
  usePatchUserSettingsPrpFormatMutation,
  useDeleteUserSettingsPrpFormatMutation,
} = userSettingsApi;
